/**
 * PropertiesStrip — single calm line of metadata under a block title.
 *
 * Replaces the inline chip cluster (priority bars, status pill, theme chips)
 * with a Notion-style summary row that reads like prose:
 *
 *   High priority · In review · 2 themes · Sarah Chen
 *
 * Click reveals the full property panel; until then, the row stays out of the
 * way and the title can breathe. Hidden entirely when nothing is set —
 * the doc shouldn't show a row that says "no properties set."
 */
import { useState } from 'react'
import type { Requirement, RequirementTheme } from '@ispot/shared'
import { REQUIREMENT_PRIORITY_LABELS, REQUIREMENT_STATUS_LABELS } from '../constants'

interface Props {
  requirement: Requirement
  themes?: RequirementTheme[]
  /** Optional persona name shown as a trailing token, when applicable. */
  personaName?: string
  /** Click on the strip — hook for opening a fuller property panel later. */
  onOpenProperties?: () => void
}

export function PropertiesStrip({ requirement: r, themes = [], personaName, onOpenProperties }: Props) {
  const [open, setOpen] = useState(false)
  const tokens = useTokens(r, themes, personaName)
  if (tokens.length === 0) return null

  const handleClick = () => {
    if (onOpenProperties) onOpenProperties()
    else setOpen(o => !o)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-expanded={open}
      aria-label="Open properties"
      className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 cursor-pointer border-0 bg-transparent text-left p-0 transition-colors"
      style={{
        fontSize: 13,
        lineHeight: 1.5,
        color: 'var(--color-fg-muted)',
        fontFamily: 'var(--font-body)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fg-secondary)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fg-muted)')}
    >
      {tokens.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <span aria-hidden style={{ color: 'var(--color-fg-faint, var(--color-fg-muted))', opacity: 0.5 }}>·</span>}
          {t.dot && (
            <span
              aria-hidden
              style={{
                width: 6, height: 6, borderRadius: 999,
                background: t.dot, flexShrink: 0,
              }}
            />
          )}
          <span>{t.label}</span>
        </span>
      ))}
      <span aria-hidden style={{ marginLeft: 4, opacity: 0.55 }}>›</span>
    </button>
  )
}

interface Token { label: string; dot?: string }

function useTokens(r: Requirement, themes: RequirementTheme[], personaName?: string): Token[] {
  const tokens: Token[] = []

  if (r.priority) {
    tokens.push({ label: REQUIREMENT_PRIORITY_LABELS[r.priority] })
  }

  if (r.status !== 'backlog') {
    tokens.push({ label: REQUIREMENT_STATUS_LABELS[r.status] })
  }

  if (r.themeIds.length > 0) {
    const primary = themes.find(t => t.id === r.primaryThemeId)
    if (primary) {
      tokens.push({ label: primary.name, dot: primary.color ?? undefined })
      const remaining = r.themeIds.length - 1
      if (remaining > 0) tokens.push({ label: `+${remaining} more` })
    } else {
      tokens.push({ label: `${r.themeIds.length} theme${r.themeIds.length === 1 ? '' : 's'}` })
    }
  }

  if (personaName) {
    tokens.push({ label: personaName })
  }

  return tokens
}
